/**
 * babybuba gift options — cart sync for paid gift line items and noshi attribute.
 */

const GIFT_OPTION_FLAG = '_gift_option';
const GIFT_OPTION_KEY = '_gift_option_key';
const NOSHI_ATTRIBUTE = 'gift_noshi';
const NOSHI_VALUE = '希望する';

function formatMoney(cents, format) {
  if (typeof cents === 'string') {
    cents = cents.replace('.', '');
  }

  const formatString =
    format ||
    window.themeVariables?.settings?.moneyWithCurrencyFormat ||
    window.themeVariables?.settings?.moneyFormat ||
    '{{amount}}';
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const match = formatString.match(placeholderRegex);

  if (!match) {
    return String(cents / 100);
  }

  const formatWithDelimiters = (number, precision = 0, thousands = ',', decimal = '.') => {
    if (Number.isNaN(number) || number == null) {
      return '0';
    }

    const fixed = (number / 100).toFixed(precision);
    const parts = fixed.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
    const centsPart = parts[1] ? decimal + parts[1] : '';
    return dollars + centsPart;
  };

  let value = '';
  switch (match[1]) {
    case 'amount_no_decimals':
    case 'amount':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
    case 'amount_with_apostrophe_separator':
      value = formatWithDelimiters(cents, 2, "'", '.');
      break;
    default:
      value = formatWithDelimiters(cents, 0);
  }

  return formatString.replace(placeholderRegex, value);
}

class GiftOptions extends HTMLElement {
  #syncingInputs = new Set();

  connectedCallback() {
    this.addEventListener('change', this.#onChange);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
  }

  #onChange = async (event) => {
    const input = event.target;

    if (!input.matches('[data-gift-option], [data-gift-noshi]')) {
      return;
    }

    if (this.#syncingInputs.has(input)) {
      input.checked = !input.checked;
      return;
    }

    this.#clearError();

    if (input.hasAttribute('data-gift-noshi')) {
      await this.#runSync(input, () => this.#updateNoshi(input.checked));
      return;
    }

    const adding = input.checked;
    this.#adjustTotalOptimistically(input, adding);

    await this.#runSync(input, async () => {
      if (adding) {
        await this.#addGiftLine(input);
      } else {
        await this.#removeGiftLine(input);
      }
    }, () => {
      input.checked = !adding;
      this.#adjustTotalOptimistically(input, !adding);
    });
  };

  async #runSync(input, task, onError) {
    this.#syncingInputs.add(input);
    input.closest('label')?.classList.add('is-syncing');

    try {
      await task();
    } catch (error) {
      onError?.();
      this.#showError(error.message || '更新に失敗しました');
    } finally {
      this.#syncingInputs.delete(input);
      input.closest('label')?.classList.remove('is-syncing');
    }
  }

  #showError(message) {
    const errorEl = this.querySelector('[data-gift-error]');
    if (!errorEl) return;
    errorEl.textContent = message;
    errorEl.hidden = false;
  }

  #clearError() {
    const errorEl = this.querySelector('[data-gift-error]');
    if (!errorEl) return;
    errorEl.textContent = '';
    errorEl.hidden = true;
  }

  #moneyFormat(withCurrency = true) {
    const settings = window.themeVariables?.settings || {};
    if (withCurrency && settings.currencyCodeEnabled) {
      return settings.moneyWithCurrencyFormat;
    }
    return settings.moneyFormat || settings.moneyWithCurrencyFormat;
  }

  #getCartTotalCents() {
    return Number.parseInt(this.dataset.cartTotal, 10) || 0;
  }

  #setCartTotalCents(cents) {
    this.dataset.cartTotal = String(cents);
    this.#updateMoneyDisplays({ total_price: cents });
  }

  #adjustTotalOptimistically(input, adding) {
    const price = Number.parseInt(input.dataset.variantPrice, 10) || 0;
    const delta = adding ? price : -price;
    this.#setCartTotalCents(this.#getCartTotalCents() + delta);
  }

  #updateMoneyDisplays(cart) {
    if (cart?.total_price == null) {
      return;
    }

    const totalWithCurrency = formatMoney(cart.total_price, this.#moneyFormat(true));
    const totalPlain = formatMoney(cart.total_price, this.#moneyFormat(false));

    document.querySelectorAll('[data-gift-cart-total]').forEach((element) => {
      element.textContent = totalWithCurrency;
    });

    document.querySelectorAll('.cart-drawer__button-price').forEach((element) => {
      element.textContent = totalPlain;
    });
  }

  #dispatchCartChange(cart) {
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', {
        bubbles: true,
        detail: {
          baseEvent: 'gift-options:change',
          cart,
        },
      })
    );
  }

  #syncCartInBackground() {
    fetch(`${Shopify.routes.root}cart.js`)
      .then((response) => response.json())
      .then((cart) => {
        this.#setCartTotalCents(cart.total_price);
        this.#dispatchCartChange(cart);
      })
      .catch(() => {});
  }

  async #addGiftLine(input) {
    const variantId = Number.parseInt(input.dataset.variantId, 10);
    const optionKey = input.dataset.optionKey;

    const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [
          {
            id: variantId,
            quantity: 1,
            properties: {
              [GIFT_OPTION_FLAG]: '1',
              [GIFT_OPTION_KEY]: optionKey,
            },
          },
        ],
      }),
    });

    const payload = await response.json();

    if (!response.ok) {
      throw new Error(payload.description || payload.message || 'ギフトの追加に失敗しました');
    }

    const addedItem = payload.items?.find(
      (item) => item.properties?.[GIFT_OPTION_KEY] === optionKey
    );
    if (addedItem?.key) {
      input.dataset.lineKey = addedItem.key;
    }

    this.#syncCartInBackground();
  }

  async #removeGiftLine(input) {
    let lineKey = input.dataset.lineKey;

    if (!lineKey) {
      const cart = await fetch(`${Shopify.routes.root}cart.js`).then((response) => response.json());
      lineKey = cart.items.find(
        (item) => item.properties?.[GIFT_OPTION_KEY] === input.dataset.optionKey
      )?.key;
    }

    if (!lineKey) {
      return;
    }

    const response = await fetch(`${Shopify.routes.root}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: lineKey,
        quantity: 0,
      }),
    });

    const payload = await response.json();

    if (!response.ok) {
      throw new Error(payload.description || payload.message || 'ギフトの削除に失敗しました');
    }

    delete input.dataset.lineKey;

    if (typeof payload.total_price === 'number') {
      this.#setCartTotalCents(payload.total_price);
      this.#dispatchCartChange(payload);
    } else {
      this.#syncCartInBackground();
    }
  }

  async #updateNoshi(checked) {
    const response = await fetch(`${Shopify.routes.root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        attributes: {
          [NOSHI_ATTRIBUTE]: checked ? NOSHI_VALUE : '',
        },
      }),
    });

    const payload = await response.json();

    if (!response.ok) {
      throw new Error(payload.description || payload.message || 'のし設定の更新に失敗しました');
    }

    if (typeof payload.total_price === 'number') {
      this.#dispatchCartChange(payload);
    }
  }
}

if (!window.customElements.get('gift-options')) {
  window.customElements.define('gift-options', GiftOptions);
}
